/**
 * AdvancedConfigApp.tsx - Configuración Avanzada Multi-Canal
 * TODO: Integrar con backend cuando estén listos los endpoints
 */

import React, { useState } from 'react';
import { Card, Nav, Badge, Row, Col, Button, Alert, Table, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe,
  faRobot,
  faChartLine,
  faFileAlt,
  faCheckCircle,
  faExclamationTriangle,
  faCog,
  faComments,
  faCamera,
  faShare,
  faPaperPlane,
  faPlus,
  faTimesCircle,
  faLock
} from '@fortawesome/free-solid-svg-icons';

// ═══════════════════════════════════════════════════════════════════════════
// MOCK DATA (datos de referencia simples)
// ═══════════════════════════════════════════════════════════════════════════

const MOCK_CHANNELS = [
  { id: 1, name: 'WASP', type: 'wasp', status: 'connected', messages: 15420, icon: faComments, color: '#25D366', mandatory: true },
  { id: 2, name: 'Instagram', type: 'instagram', status: 'disconnected', messages: 0, icon: faCamera, color: '#E4405F', mandatory: false },
  { id: 3, name: 'Facebook', type: 'facebook', status: 'disconnected', messages: 0, icon: faShare, color: '#1877F2', mandatory: false },
  { id: 4, name: 'Telegram', type: 'telegram', status: 'disconnected', messages: 0, icon: faPaperPlane, color: '#0088CC', mandatory: false }
];

const MOCK_LOGS = [
  { id: 1, level: 'info', module: 'wasp', message: 'Mensaje enviado exitosamente', time: '10:45:32' },
  { id: 2, level: 'error', module: 'telegram', message: 'Error de autenticación', time: '10:42:15' },
  { id: 3, level: 'warning', module: 'contacts', message: 'Contacto duplicado detectado', time: '10:40:08' },
  { id: 4, level: 'info', module: 'backup', message: 'Backup completado', time: '03:02:15' }
];

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const AdvancedConfigApp: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('channels');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  const [channels, setChannels] = useState(MOCK_CHANNELS);
  
  // Form state
  const [formData, setFormData] = useState({
    apiKey: '',
    accessToken: '',
    pageId: ''
  });

  const tabs = [
    { id: 'channels', label: 'Canales', icon: faGlobe, badge: channels.filter(c => c.status === 'connected').length },
    { id: 'automation', label: 'Automatización', icon: faRobot },
    { id: 'stats', label: 'Estadísticas', icon: faChartLine },
    { id: 'logs', label: 'Logs', icon: faFileAlt, badge: MOCK_LOGS.filter(l => l.level === 'error').length }
  ];

  // ═════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═════════════════════════════════════════════════════════════════════════

  const handleConnect = (channel: any) => {
    setSelectedChannel(channel);
    setFormData({ apiKey: '', accessToken: '', pageId: '' });
    setShowConnectModal(true);
  };

  const handleDisconnect = (channelId: number) => {
    setChannels(prev => prev.map(ch => 
      ch.id === channelId ? { ...ch, status: 'disconnected', messages: 0 } : ch
    ));
  };

  const handleSubmitConnection = () => {
    if (!formData.apiKey || !formData.accessToken) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    // Simular conexión exitosa
    setChannels(prev => prev.map(ch => 
      ch.id === selectedChannel.id 
        ? { 
            ...ch, 
            status: 'connected',
            messages: Math.floor(Math.random() * 1000) // Mock messages
          } 
        : ch
    ));

    alert(`✅ ${selectedChannel.name} conectado exitosamente!\n\nEn producción, aquí se enviarían las credenciales al backend.`);
    setShowConnectModal(false);
    setFormData({ apiKey: '', accessToken: '', pageId: '' });
  };

  // ═════════════════════════════════════════════════════════════════════════
  // TAB: CANALES
  // ═════════════════════════════════════════════════════════════════════════
  
  const renderChannels = () => (
    <div>
      <h5 className="mb-4">Canales de Mensajería</h5>
      <Row className="g-3">
        {channels.map(channel => (
          <Col key={channel.id} md={6} lg={3}>
            <Card style={{ 
              border: 'none', 
              borderRadius: '12px', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              opacity: channel.mandatory ? 1 : (channel.status === 'connected' ? 1 : 0.8)
            }}>
              <Card.Body>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: `${channel.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    color: channel.color
                  }}>
                    <FontAwesomeIcon icon={channel.icon} />
                  </div>
                  <div>
                    <h6 className="mb-0">
                      {channel.name}
                      {channel.mandatory && (
                        <Badge bg="primary" className="ms-2" style={{ fontSize: '0.6rem' }}>
                          Core
                        </Badge>
                      )}
                    </h6>
                    <Badge bg={channel.status === 'connected' ? 'success' : 'secondary'} style={{ fontSize: '0.7rem' }}>
                      {channel.status === 'connected' ? '✓ Conectado' : 'Desconectado'}
                    </Badge>
                  </div>
                </div>
                
                {channel.status === 'connected' && (
                  <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }} className="mb-3">
                    <strong>{channel.messages.toLocaleString()}</strong> mensajes hoy
                  </div>
                )}

                {channel.mandatory ? (
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    style={{ width: '100%', borderRadius: '6px' }}
                    disabled
                  >
                    <FontAwesomeIcon icon={faLock} className="me-1" />
                    Servicio Mandatorio
                  </Button>
                ) : (
                  <Button 
                    variant={channel.status === 'connected' ? 'outline-danger' : 'primary'} 
                    size="sm" 
                    style={{ width: '100%', borderRadius: '6px' }}
                    onClick={() => channel.status === 'connected' ? handleDisconnect(channel.id) : handleConnect(channel)}
                  >
                    {channel.status === 'connected' ? 'Desconectar' : 'Conectar'}
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // TAB: AUTOMATIZACIÓN
  // ═════════════════════════════════════════════════════════════════════════
  
  const renderAutomation = () => (
    <div>
      <h5 className="mb-4">Automatización por Canal</h5>
      
      <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label><strong>Seleccionar Canal:</strong></Form.Label>
            <Form.Select>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="telegram">Telegram</option>
            </Form.Select>
          </Form.Group>

          <Alert variant="info" style={{ fontSize: '0.9rem' }}>
            <strong>Opción 1:</strong> Usar flujos de WASP (compartidos)<br/>
            <strong>Opción 2:</strong> Configurar flujos propios para este canal
          </Alert>

          <div className="mt-3">
            <p style={{ fontSize: '0.9rem', color: 'var(--bs-secondary)' }}>
              <em>Funcionalidad en desarrollo. Aquí podrás configurar el comportamiento del bot por cada canal.</em>
            </p>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // TAB: ESTADÍSTICAS
  // ═════════════════════════════════════════════════════════════════════════
  
  const renderStats = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Estadísticas Multi-Canal</h5>
        <Button variant="outline-primary" size="sm">
          Exportar CSV
        </Button>
      </div>

      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center' }}>
            <Card.Body>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6366f1' }}>15,420</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>Mensajes Totales</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center' }}>
            <Card.Body>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>94.2%</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>Tasa de Respuesta</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center' }}>
            <Card.Body>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007bff' }}>1.2 min</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>Tiempo Promedio</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center' }}>
            <Card.Body>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffc107' }}>856</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>Contactos Activos</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Card.Body>
          <h6 className="mb-3">Mensajes por Canal</h6>
          <div className="mb-2">
            <div className="d-flex justify-content-between mb-1">
              <span style={{ fontSize: '0.9rem' }}>WASP</span>
              <strong>15,420</strong>
            </div>
            <div style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
              <div style={{ width: '100%', height: '100%', backgroundColor: '#25D366', borderRadius: '4px' }}></div>
            </div>
          </div>
          <div className="mb-2">
            <div className="d-flex justify-content-between mb-1">
              <span style={{ fontSize: '0.9rem' }}>Instagram</span>
              <strong>0</strong>
            </div>
            <div style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
              <div style={{ width: '0%', height: '100%', backgroundColor: '#E4405F', borderRadius: '4px' }}></div>
            </div>
          </div>
          <div className="mb-2">
            <div className="d-flex justify-content-between mb-1">
              <span style={{ fontSize: '0.9rem' }}>Facebook</span>
              <strong>0</strong>
            </div>
            <div style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
              <div style={{ width: '0%', height: '100%', backgroundColor: '#1877F2', borderRadius: '4px' }}></div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // TAB: LOGS
  // ═════════════════════════════════════════════════════════════════════════
  
  const renderLogs = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">System Logs (Últimos 10)</h5>
        <Button variant="outline-primary" size="sm" onClick={() => alert('TODO: Export logs')}>
          Export CSV
        </Button>
      </div>

      <div style={{ 
        backgroundColor: '#1e1e1e', 
        borderRadius: '8px', 
        padding: '16px',
        maxHeight: '500px',
        overflowY: 'auto',
        fontFamily: 'monospace',
        fontSize: '0.85rem'
      }}>
        {MOCK_LOGS.map(log => (
          <div key={log.id} style={{ 
            marginBottom: '8px',
            color: log.level === 'error' ? '#f85149' : log.level === 'warning' ? '#d29922' : '#79c0ff'
          }}>
            <span style={{ color: '#8b949e' }}>[{log.time}]</span>{' '}
            <span style={{ color: log.level === 'error' ? '#f85149' : log.level === 'warning' ? '#d29922' : '#79c0ff' }}>
              [{log.level.toUpperCase()}]
            </span>{' '}
            <span style={{ color: '#c9d1d9' }}>[{log.module}]</span>{' '}
            <span style={{ color: '#c9d1d9' }}>{log.message}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER PRINCIPAL
  // ═════════════════════════════════════════════════════════════════════════

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--bs-body-bg)' }}>
      {/* Warning */}
      <Alert variant="warning" style={{ margin: '16px 16px 0 16px', borderRadius: '8px' }} dismissible>
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        <strong>Modo Desarrollo:</strong> Datos de prueba. Backend pendiente de integración.
      </Alert>

      {/* Header */}
      <div style={{ padding: '24px 24px 0 24px', borderBottom: '1px solid var(--bs-border-color)' }}>
        <div className="mb-3">
          <h4 style={{ margin: 0, fontWeight: 600 }}>
            <FontAwesomeIcon icon={faGlobe} className="me-2" style={{ color: '#6366f1' }} />
            Configuración Avanzada
          </h4>
          <p style={{ margin: '4px 0 0 0', color: 'var(--bs-secondary)', fontSize: '0.9rem' }}>
            Extiende las capacidades de WASP a otros canales de mensajería
          </p>
        </div>

        {/* Tabs */}
        <Nav variant="tabs" style={{ borderBottom: 'none' }}>
          {tabs.map(tab => (
            <Nav.Item key={tab.id}>
              <Nav.Link
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  cursor: 'pointer',
                  padding: '12px 20px',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '3px solid #6366f1' : '3px solid transparent',
                  backgroundColor: 'transparent',
                  color: activeTab === tab.id ? '#6366f1' : 'var(--bs-secondary)',
                  fontWeight: activeTab === tab.id ? 600 : 400
                }}
              >
                <FontAwesomeIcon icon={tab.icon} className="me-2" />
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <Badge bg="primary" className="ms-2" style={{ fontSize: '0.7rem' }}>
                    {tab.badge}
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {activeTab === 'channels' && renderChannels()}
        {activeTab === 'automation' && renderAutomation()}
        {activeTab === 'stats' && renderStats()}
        {activeTab === 'logs' && renderLogs()}
      </div>

      {/* Modal: Conectar Canal */}
      <Modal 
        show={showConnectModal} 
        onHide={() => setShowConnectModal(false)} 
        centered
        style={{ zIndex: 99999 }}
        backdropClassName="modal-backdrop-high"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={selectedChannel?.icon} className="me-2" style={{ color: selectedChannel?.color }} />
            Conectar {selectedChannel?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>API Key <span style={{ color: 'red' }}>*</span></Form.Label>
              <Form.Control 
                type="text" 
                placeholder="Ingresa tu API Key"
                value={formData.apiKey}
                onChange={(e) => setFormData({...formData, apiKey: e.target.value})}
              />
              <Form.Text className="text-muted">
                Obtén tu API Key desde {selectedChannel?.name} Developer Portal
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Access Token <span style={{ color: 'red' }}>*</span></Form.Label>
              <Form.Control 
                type="text" 
                placeholder="Ingresa tu Access Token"
                value={formData.accessToken}
                onChange={(e) => setFormData({...formData, accessToken: e.target.value})}
              />
            </Form.Group>
            
            {selectedChannel?.type === 'instagram' && (
              <Form.Group className="mb-3">
                <Form.Label>Instagram Page ID</Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder="Ingresa tu Page ID (opcional)"
                  value={formData.pageId}
                  onChange={(e) => setFormData({...formData, pageId: e.target.value})}
                />
              </Form.Group>
            )}
            
            <Alert variant="info" style={{ fontSize: '0.85rem' }}>
              <strong>Modo Mock:</strong> Los datos no se enviarán al backend. Solo simularemos la conexión.
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConnectModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmitConnection}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Conectar
          </Button>
        </Modal.Footer>
      </Modal>
      
      <style>{`
        .modal-backdrop-high {
          z-index: 99998 !important;
        }
        .modal {
          z-index: 99999 !important;
        }
        .modal-dialog {
          z-index: 99999 !important;
        }
      `}</style>
    </div>
  );
};

export default AdvancedConfigApp;